import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, KeyRound, Wallet, Smartphone } from "lucide-react";
import Nav from "@/components/fittin/Nav";
import Footer from "@/components/fittin/Footer";
import { formatPairCode, startSession, useSession } from "@/lib/session";

// Only same-site paths are allowed as a return target.
const safeNext = (n) => (n && n.startsWith("/") && !n.startsWith("//") ? n : "/me");

// /signin — "Sign in with your FitKey". No password, no email: either the link
// on the Wallet pass (handled on /k/:id) or a pairing code from another device.
export default function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const session = useSession();

  const [code, setCode] = useState("");
  const [state, setState] = useState("idle"); // idle | sending
  const [error, setError] = useState("");

  const complete = code.replace(/[^A-Z0-9]/g, "").length === 8;

  const submit = async (e) => {
    e?.preventDefault();
    if (!complete) {
      setError("Enter the 8-character code, e.g. ABCD-EFGH.");
      return;
    }
    setError("");
    setState("sending");
    const r = await startSession({ code });
    setState("idle");
    if (r.ok) {
      navigate(next, { replace: true });
      return;
    }
    setError(
      r.status === 401
        ? "That code is invalid or has expired. Create a new one on your other device."
        : r.status === 503
        ? "Sign-in isn’t available right now. Please try again later."
        : "Couldn’t sign you in right now. Please try again."
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <Nav />
      <main data-testid="signin-page" className="mx-auto max-w-5xl px-6 md:px-10 pt-28 md:pt-36 pb-24">
        <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
          <span className="h-px w-8 bg-black/40" />
          No password · No email
        </div>
        <h1 data-testid="signin-headline" className="mt-5 text-4xl sm:text-5xl tracking-[-0.035em] font-medium leading-[1.03]">
          Sign in with your FitKey.
        </h1>
        <p className="mt-4 max-w-xl text-[15px] text-neutral-600 leading-relaxed">
          Open your FitKey in your Wallet and tap the link, or enter a code from another device.
        </p>

        {session && (
          <div data-testid="signin-already" className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-black/10 bg-white p-4 text-[13px]">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            You’re already signed in on this device.
            <Link to="/me" data-testid="signin-open-me" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white px-4 py-2 text-[12.5px] hover:bg-neutral-800">
              Open My FitKey <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Option 1: the Wallet link */}
          <section data-testid="signin-wallet" className="glass-card rounded-3xl p-6 sm:p-7">
            <div className="grid place-items-center h-10 w-10 rounded-xl bg-[#0A0A0A] text-white">
              <Wallet className="h-[18px] w-[18px]" strokeWidth={1.6} />
            </div>
            <h2 className="mt-5 text-[19px] font-medium tracking-tight">On the phone with your pass</h2>
            <ol className="mt-4 space-y-3 text-[13.5px] text-neutral-600">
              {[
                "Open your Wallet and tap your FitKey.",
                "Open the pass details and tap the Fittin link.",
                "You’re signed in. The link is your key, nothing to type.",
              ].map((t, i) => (
                <li key={t} className="flex gap-3">
                  <span className="grid place-items-center h-5 w-5 shrink-0 rounded-full border border-black/10 bg-white text-[10.5px] text-neutral-500">
                    {i + 1}
                  </span>
                  {t}
                </li>
              ))}
            </ol>
            <p className="mt-5 text-[12px] text-neutral-500">
              No FitKey yet?{" "}
              <Link to="/#create-fitkey" data-testid="signin-create" className="underline underline-offset-2 hover:text-[#0A0A0A]">
                Create one in a minute
              </Link>
              .
            </p>
          </section>

          {/* Option 2: pairing code */}
          <form onSubmit={submit} noValidate data-testid="signin-code-form" className="glass-card rounded-3xl p-6 sm:p-7">
            <div className="grid place-items-center h-10 w-10 rounded-xl bg-white border border-black/10">
              <Smartphone className="h-[18px] w-[18px]" strokeWidth={1.6} />
            </div>
            <h2 className="mt-5 text-[19px] font-medium tracking-tight">Enter a code from another device</h2>
            <p className="mt-2 text-[13px] text-neutral-500 leading-relaxed">
              On a device where you’re signed in, open My FitKey and choose “Sign in on another device”.
            </p>
            <label htmlFor="signin-code" className="mt-6 block text-[10.5px] uppercase tracking-[0.2em] text-neutral-500">
              Pairing code
            </label>
            <input
              id="signin-code"
              data-testid="signin-code-input"
              value={code}
              onChange={(e) => {
                setCode(formatPairCode(e.target.value));
                if (error) setError("");
              }}
              placeholder="ABCD-EFGH"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              maxLength={9}
              className={`mt-1.5 w-full rounded-xl border bg-white px-4 h-14 font-mono text-[22px] tracking-[0.18em] text-center uppercase text-[#0A0A0A] placeholder:text-neutral-200 outline-none focus:border-black/50 transition-colors ${
                error ? "border-red-300" : "border-black/10"
              }`}
            />
            {error && (
              <div data-testid="signin-code-error" className="mt-2 text-[12px] text-red-600">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={state === "sending"}
              data-testid="signin-code-submit"
              className="mt-5 w-full group inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white h-12 text-[14px] hover:bg-neutral-800 transition-all active:scale-[0.98] disabled:opacity-60"
            >
              {state === "sending" ? (
                <>
                  <span className="h-3 w-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Signing in
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4" />
                  Sign in
                </>
              )}
            </button>
            <p className="mt-3 text-center text-[11.5px] text-neutral-400">Codes work once and expire after 5 minutes.</p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
